import { h } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import { batch } from '@preact/signals'
import { DirectUpload } from '@rails/activestorage'
import { rails_direct_uploads_path } from '../../../routes'
import { replaceTextEmoji, genClientId, buildOptimistic, TYPING_STOP_DELAY_MS } from '@tshmanager/chat-shared'
import { ReplyBar } from './ReplyBar.jsx'
import { AttachmentRow } from './AttachmentRow.jsx'
import { TaskReferenceStrip } from './TaskReferenceStrip.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'
import {
  htmlToMarkdown,
  applyInlineFormat,
  applyWrapFormat,
  insertLink,
  insertMentionChip,
  insertText,
  getCaretContext,
  clearEditor,
  focusEnd,
} from '../chat-markdown-editor.js'

const TYPING_STOP_DELAY = TYPING_STOP_DELAY_MS

const EMOJI_LIST = [
  '😀','😂','🥲','😍','🤩','😎','🥳','😇','🤔','😅',
  '😆','😏','😒','😔','😢','😡','🤯','🥴','😴','🤢',
  '👍','👎','👋','🙌','👏','🤝','🙏','💪','👀','❤️',
  '🔥','✅','❌','⚡','🎉','🎊','🚀','💡','💬','📎',
  '🏠','📝','🔑','📅','🗓️','⏰','💼','📊','🔧','📌',
  '✨','🎯','💯','👋','🌟','💤','🍕','☕','🎵','🎮',
]

/**
 * Composer — WYSIWYG message input with contenteditable editor.
 *
 * The editor shows formatted text (bold/italic/mention chips).
 * The underlying markdown string is kept in the `inputText` signal and
 * sent to the server on submit. This is "dual bookkeeping":
 *   - display layer: contenteditable innerHTML (rich HTML)
 *   - storage layer: `inputText` signal (markdown string)
 *
 * Props: { store, cable, roomId, personId }
 */
export function Composer({ store, cable, roomId, personId }) {
  const inputText          = useSignal('')
  const emojiPickerOpen    = useSignal(false)
  const isDragging         = useSignal(false)
  const mentionQuery       = useSignal(null)   // null = inactive
  const mentionResults     = useSignal([])
  const mentionIndex       = useSignal(0)
  const pendingAttachments = useSignal([])
  const sendingRef         = useRef(false)

  const editorRef      = useRef(null)
  const fileInputRef   = useRef(null)
  const emojiWrapRef   = useRef(null)
  const typingTimerRef = useRef(null)
  const isTypingRef    = useRef(false)

  // Active format state: updated on every selection change inside the editor.
  // Used to show toolbar buttons as "active" when the caret is inside formatted text.
  const activeFormats = useSignal({ bold: false, italic: false, underline: false, strikeThrough: false })

  // Auto-focus on mount / room change
  useEffect(() => {
    requestAnimationFrame(() => focusEnd(editorRef.current))
  }, [roomId]) // eslint-disable-line

  // Track active formats on selection change (caret moved / text selected)
  useEffect(() => {
    function onSelectionChange() {
      const sel = document.getSelection()
      if (!sel || !editorRef.current) return
      // Only update when selection is inside the editor
      const node = sel.anchorNode
      if (!node || !editorRef.current.contains(node)) return
      activeFormats.value = {
        bold:          document.queryCommandState('bold'),
        italic:        document.queryCommandState('italic'),
        underline:     document.queryCommandState('underline'),
        strikeThrough: document.queryCommandState('strikeThrough'),
      }
    }
    document.addEventListener('selectionchange', onSelectionChange)
    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, []) // eslint-disable-line

  // Close emoji picker on outside click
  useEffect(() => {
    if (!emojiPickerOpen.value) return
    function onDocClick(e) {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(e.target)) {
        emojiPickerOpen.value = false
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [emojiPickerOpen.value]) // eslint-disable-line

  // Cleanup typing indicator on unmount / room change
  useEffect(() => {
    return () => {
      if (isTypingRef.current) {
        chatApi.typing.send(roomId, false).catch(() => { /* ignore */ })
      }
      clearTimeout(typingTimerRef.current)
    }
  }, [roomId]) // eslint-disable-line

  // ── Typing debounce ────────────────────────────────────────────────────

  function handleTyping() {
    if (!isTypingRef.current) {
      isTypingRef.current = true
      chatApi.typing.send(roomId, true).catch(() => { /* ignore */ })
    }
    clearTimeout(typingTimerRef.current)
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false
      chatApi.typing.send(roomId, false).catch(() => { /* ignore */ })
    }, TYPING_STOP_DELAY)
  }

  // ── Input handler ──────────────────────────────────────────────────────

  function handleInput() {
    // Serialize editor HTML → markdown (turndown)
    inputText.value = htmlToMarkdown(editorRef.current)
    handleTyping()

    // @mention detection — read caret position from Selection API
    const { text } = getCaretContext()
    const match = text.match(/@([^\s@]*)$/)
    if (match) {
      const q = match[1]
      mentionQuery.value = q
      mentionIndex.value = 0
      fetchMentions(q)
    } else {
      mentionQuery.value   = null
      mentionResults.value = []
      mentionIndex.value   = 0
    }
  }

  // ── @mention ───────────────────────────────────────────────────────────

  function fetchMentions(q) {
    chatApi.persons.search(roomId, q).then(async res => {
      if (res.ok) {
        const data = await res.json
        mentionResults.value = Array.isArray(data) ? data : (data.persons ?? [])
        mentionIndex.value   = 0
      }
    }).catch(() => { mentionResults.value = [] })
  }

  function doInsertMention(person) {
    insertMentionChip(person, editorRef.current)
    inputText.value      = htmlToMarkdown(editorRef.current)
    mentionQuery.value   = null
    mentionResults.value = []
    mentionIndex.value   = 0
  }

  // ── Key handling ───────────────────────────────────────────────────────

  function handleKeyDown(e) {
    const results = mentionResults.value
    const active  = mentionQuery.value !== null && results.length > 0

    if (active) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        mentionIndex.value = (mentionIndex.value + 1) % results.length
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        mentionIndex.value = (mentionIndex.value - 1 + results.length) % results.length
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        doInsertMention(results[mentionIndex.value])
        return
      }
      if (e.key === 'Escape') {
        mentionQuery.value   = null
        mentionResults.value = []
        mentionIndex.value   = 0
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault()
      doSend()
      return
    }
    if (e.key === 'Escape') {
      emojiPickerOpen.value = false
      mentionQuery.value    = null
    }
  }

  // ── Formatting toolbar ─────────────────────────────────────────────────

  function updateActiveFormats() {
    activeFormats.value = {
      bold:          document.queryCommandState('bold'),
      italic:        document.queryCommandState('italic'),
      underline:     document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
    }
  }

  function fmt(e, command) {
    e.preventDefault()           // keep focus in editor
    editorRef.current?.focus()
    applyInlineFormat(command)
    inputText.value = htmlToMarkdown(editorRef.current)
    updateActiveFormats()
  }

  function fmtWrap(e, open, close) {
    e.preventDefault()
    editorRef.current?.focus()
    applyWrapFormat(open, close)
    inputText.value = htmlToMarkdown(editorRef.current)
  }

  function fmtLink(e) {
    e.preventDefault()
    editorRef.current?.focus()
    insertLink()
    inputText.value = htmlToMarkdown(editorRef.current)
  }

  function doInsertEmoji(emoji) {
    editorRef.current?.focus()
    insertText(emoji)
    inputText.value       = htmlToMarkdown(editorRef.current)
    emojiPickerOpen.value = false
  }

  // ── Paste — images go to attachment upload; text is stripped to plain ──

  function handlePaste(e) {
    const items = e.clipboardData?.items
    if (items) {
      const imageItems = Array.from(items).filter(
        item => item.kind === 'file' && item.type.startsWith('image/')
      )
      if (imageItems.length) {
        e.preventDefault()
        imageItems.forEach(item => {
          const file = item.getAsFile()
          if (file) uploadFile(file)
        })
        return
      }
    }
    // No image — strip outer HTML, keep plain text
    e.preventDefault()
    const text = e.clipboardData?.getData('text/plain')
    if (text) {
      document.execCommand('insertText', false, text)
      inputText.value = htmlToMarkdown(editorRef.current)
    }
  }

  // ── File upload ────────────────────────────────────────────────────────

  function openFilePicker() {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  function handleFileChange(e) {
    Array.from(e.target.files || []).forEach(uploadFile)
    e.target.value = ''
  }

  function uploadFile(file) {
    const key = `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`
    const att = {
      _localKey: key,
      filename:  file.name,
      mimeType:  file.type,
      isImage:   file.type.startsWith('image/'),
      localUri:  file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      progress:  0,
      error:     null,
      signedId:  null,
    }
    pendingAttachments.value = [...pendingAttachments.value, att]

    const upload = new DirectUpload(file, rails_direct_uploads_path(), {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener('progress', (ev) => {
          if (!ev.lengthComputable) return
          const pct = Math.round((ev.loaded / ev.total) * 100)
          pendingAttachments.value = pendingAttachments.value.map(a =>
            a._localKey === key ? { ...a, progress: pct } : a
          )
        })
      },
    })

    upload.create((error, blob) => {
      if (error) {
        pendingAttachments.value = pendingAttachments.value.map(a =>
          a._localKey === key ? { ...a, error: String(error), progress: 100 } : a
        )
      } else {
        pendingAttachments.value = pendingAttachments.value.map(a =>
          a._localKey === key ? { ...a, signedId: blob.signed_id, progress: 100 } : a
        )
      }
    })
  }

  function removeAttachment(att) {
    pendingAttachments.value = pendingAttachments.value.filter(a => a._localKey !== att._localKey)
    if (att.localUri) URL.revokeObjectURL(att.localUri)
  }

  // ── Drag-and-drop ──────────────────────────────────────────────────────

  function handleDragOver(e) {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault()
    }
  }

  function handleDragEnter(e) {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault()
      isDragging.value = true
    }
  }

  function handleDragLeave(e) {
    // Only deactivate when the pointer truly leaves the composer element,
    // not when it moves between child nodes inside it.
    const composer = e.currentTarget
    if (!composer.contains(e.relatedTarget)) {
      isDragging.value = false
    }
  }

  function handleDrop(e) {
    e.preventDefault()
    isDragging.value = false
    const files = e.dataTransfer?.files
    if (files?.length) {
      Array.from(files).forEach(uploadFile)
    }
  }

  // ── Send ───────────────────────────────────────────────────────────────

  async function doSend() {
    if (sendingRef.current) return
    const body       = replaceTextEmoji(inputText.value.trim())
    const atts       = pendingAttachments.value
    const signedIds  = atts.filter(a => a.signedId).map(a => a.signedId)

    if (!body && !signedIds.length) return
    if (atts.some(a => !a.signedId && !a.error)) return  // still uploading

    const replyTo   = store.replyTo.value
    const replyToId = replyTo?.id ?? null
    const clientId = genClientId()
    const optimistic = {
      ...buildOptimistic({
        id: -Date.now(),
        roomId,
        body,
        clientMessageId: clientId,
        personId,
        personName: store.personName ?? '',
        replyTo: replyTo ?? null,
        signedIds,
      }),
      roomId,
      _clientId: clientId,
      _retryData: { body, attachments: signedIds, reply_to_id: replyToId },
    }

    batch(() => {
      store.pendingMsgs.value = [...store.pendingMsgs.value, optimistic]
      store.replyTo.value     = null
    })

    // Clear editor
    inputText.value          = ''
    pendingAttachments.value = []
    clearEditor(editorRef.current)

    // Stop typing indicator
    clearTimeout(typingTimerRef.current)
    if (isTypingRef.current) {
      isTypingRef.current = false
      chatApi.typing.send(roomId, false).catch(() => { /* ignore */ })
    }

    sendingRef.current = true
    try {
      await chatApi.messages.create(roomId, {
        body,
        attachments: signedIds,
        reply_to_id: replyToId,
        client_message_id: clientId,
      })
      store.pendingMsgs.value = store.pendingMsgs.value.filter(m => m._clientId !== clientId)
    } catch {
      store.pendingMsgs.value = store.pendingMsgs.value.map(m =>
        m._clientId === clientId ? { ...m, _sendStatus: 'failed' } : m
      )
    } finally {
      sendingRef.current = false
      requestAnimationFrame(() => focusEnd(editorRef.current))
    }
  }

  const isEmpty  = !inputText.value.trim()
  const canSend  = !isEmpty || pendingAttachments.value.some(a => a.signedId)

  return (
    <div
      class={`chat-composer${isDragging.value ? ' is-drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop overlay */}
      <div class="chat-composer-drop-overlay">
        <i class="bi bi-paperclip fs-4 me-2" />
        {t('chat.messages.drop_files_here')}
      </div>

      {/* Reply bar */}
      <ReplyBar store={store} />

      {/* Task reference strip */}
      <TaskReferenceStrip inputText={inputText.value} store={store} />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        class="d-none"
        onChange={handleFileChange}
      />

      {/* @mention dropdown */}
      {mentionQuery.value !== null && mentionResults.value.length > 0 && (
        <div class="chat-mention-dropdown">
          {mentionResults.value.slice(0, 8).map((person, idx) => (
            <button
              key={person.id}
              type="button"
              class={`chat-mention-item${idx === mentionIndex.value ? ' is-active' : ''}`}
              onMouseDown={e => { e.preventDefault(); doInsertMention(person) }}
              onMouseEnter={() => { mentionIndex.value = idx }}
            >
              {person.avatar_url
                ? <img src={person.avatar_url} class="chat-mention-avatar" width={24} height={24} alt="" />
                : <span class="chat-mention-avatar chat-mention-avatar--initials">{person.name.slice(0,1).toUpperCase()}</span>
              }
              <span class="chat-mention-name">{person.name}</span>
            </button>
          ))}
        </div>
      )}

      <div class="chat-composer-input-wrap">
        {/* Attachment chips */}
        <AttachmentRow
          attachments={pendingAttachments.value}
          onRemove={removeAttachment}
        />

        {/* WYSIWYG contenteditable editor */}
        <div class="chat-composer-editor-row">
          <div
            ref={editorRef}
            class={`chat-md-editor${isEmpty ? ' is-empty' : ''}`}
            contentEditable="true"
            autocapitalize="sentences"
            data-placeholder={t('chat.messages.placeholder')}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </div>

        {/* Toolbar */}
        <div class="chat-composer-toolbar">
          <div class="chat-composer-toolbar-left">
            <button type="button"
              class={`chat-format-btn${activeFormats.value.bold ? ' is-active' : ''}`}
              title={t('chat.messages.format.bold')}
              onMouseDown={e => fmt(e, 'bold')}>
              <strong>B</strong>
            </button>
            <button type="button"
              class={`chat-format-btn${activeFormats.value.italic ? ' is-active' : ''}`}
              title={t('chat.messages.format.italic')}
              onMouseDown={e => fmt(e, 'italic')}>
              <em>I</em>
            </button>
            <button type="button"
              class={`chat-format-btn${activeFormats.value.underline ? ' is-active' : ''}`}
              title={t('chat.messages.format.underline')}
              onMouseDown={e => fmt(e, 'underline')}>
              <u>U</u>
            </button>
            <button type="button"
              class={`chat-format-btn${activeFormats.value.strikeThrough ? ' is-active' : ''}`}
              title={t('chat.messages.format.strike')}
              onMouseDown={e => fmt(e, 'strikeThrough')}>
              <s>S</s>
            </button>
            <button type="button" class="chat-format-btn" title={t('chat.messages.format.spoiler')}
              onMouseDown={e => fmtWrap(e, '||', '||')}>
              <i class="bi bi-eye-slash" />
            </button>

            <div class="chat-format-divider" />

            <button type="button" class="chat-format-btn" title={t('chat.messages.format.link')}
              onMouseDown={fmtLink}>
              <i class="bi bi-link-45deg" />
            </button>

            <div class="chat-format-divider" />

            <button type="button" class="chat-format-btn" title={t('chat.messages.attach')}
              onClick={openFilePicker}>
              <i class="bi bi-paperclip" />
            </button>

            {/* Emoji picker */}
            <div class="chat-emoji-wrap" ref={emojiWrapRef}>
              <button
                type="button"
                class="chat-format-btn"
                title="Emoji"
                onClick={e => { e.stopPropagation(); emojiPickerOpen.value = !emojiPickerOpen.value }}
              >
                <i class="bi bi-emoji-smile" />
              </button>

              {emojiPickerOpen.value && (
                <div class="chat-emoji-picker is-open">
                  <div class="chat-emoji-grid">
                    {EMOJI_LIST.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        class="chat-emoji-btn"
                        onMouseDown={e => { e.stopPropagation(); doInsertEmoji(emoji) }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div class="chat-composer-toolbar-right">
            <span class="chat-composer-hint">Enter küld</span>
            <button
              type="button"
              class="chat-composer-send-btn"
              title={t('chat.rooms.send')}
              disabled={!canSend}
              onClick={doSend}
            >
              <i class="bi bi-send-fill" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
